// frontend/src/pages/write/Write.jsx

import "../../utils/quillInit";
import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { Context } from "../../context/Context";
import axios from "axios";
import { toast } from "react-toastify";
import "../../utils/quillInit-suggest";
import "./suggestionModeEditor.css";
import "./write.css";
import { createSlug, decodeHtml, detectPassiveVoice, detectLongSentences } from "../../utils/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faPenToSquare,
  faTrashCan,
  faUnlock,
  faRotateLeft,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import { jwtDecode } from "jwt-decode";
import SeoAnalyser from "./SeoAnalyser";
import ReadabilityAnalyser from "./ReadabilityAnalyser";
import MostUsedWords from "./MostUsedWords";
import WriterCoachPanel from "../../components/writerCoachPanel/WriterCoachPanel";
import GrammarAssistantPanel from "../../components/grammarAssistantPanel/GrammarAssistantPanel.jsx";
import FeatureImageUploadModal from "../../components/modals/FeatureImageUploadModal";
import ImageUploadModal from "../../components/modals/ImageUploadModal";
import Quill from "quill";
import ConfirmModal from "../../components/confirmModal/ConfirmModal";
import { FaCalendarAlt } from "react-icons/fa";
import { FaTimes } from "react-icons/fa";
import SidebarDraftManager from "../../components/write/SidebarDraftManager";
import MetaData from "../../components/write/MetaData";
import EditorBlock from "../../components/write/EditorBlock";
import FocusModeOverlay from "../../components/focusMode/FocusModeOverlay";
import useFocusMode from "../../components/focusMode/useFocusMode";
import FocusToggle from "../../components/focusMode/FocusToggle";

import CollaborationPanel from "../../components/collaborationPanel/CollaborationPanel";
import InlineCommentPanel from "../../components/inlineCommentPanel/InlineCommentPanel";

import {
  getSuggestionRangeAtCursor,
  getSuggestionRangeAtIndex,
  acceptAllSuggestions,
  rejectAllSuggestions,
  acceptSuggestionRange,
  rejectSuggestionRange,
} from "../../utils/suggestionMode";

import {
  STAGE_SUBMIT_LABELS,
  WRITER_STAGES,
  EDITOR_STAGES,
  LEAD_EDITOR_STAGES,
  nextStage,
  toHuman
} from "../../utils/stages";

import useAutoSave from "../../utils/autosave";

import {
  fmtTs,
  computeFinalDraftDue,
  pickSlug,
  normalizeFeaturedImageFromPost,
  getFeaturedImagePayload,
} from "./writeHelpers";

import {
  sanitizeImages,
  containsSanitizedImages,
  extractText,
  normalizeBodyHeadings,
} from "./writeHtmlUtils";

const Delta = Quill.import("delta");

{/*
const LoadingComponent = () => (
  <div style={{ textAlign: "center", paddingTop: "100px", fontSize: "18px" }}>
    Loading writer dashboard...
  </div>
);
*/}

const UnauthorizedComponent = () => (
  <div style={{ textAlign: "center", paddingTop: "100px", fontSize: "18px" }}>
    Not Authorized!
  </div>
);

export default function Write() {
  const navigate = useNavigate();
  const quillRef = useRef(null);

  const scheduleInputRef = useRef(null);

  const lastQuillInstanceRef = useRef(null);

  const { focusMode, closeFocus, toggleFocus } = useFocusMode();
  const focusToggle = (
    <FocusToggle focusMode={focusMode} toggleFocus={toggleFocus} />
  );

  const lastPasteImageToastRef = useRef(0);

  const { user, token } = useContext(Context);

  const role = Number(user?.role ?? 0);

  const canSchedule = role >= 4;

  const [currentArticleId, setCurrentArticleId] = useState(null);

  const [savedBadge, setSavedBadge] = useState("");
  const [showRestore, setShowRestore] = useState(false);

  const dedupeByValue = (arr = []) =>
    Array.from(new Map(arr.filter(Boolean).map((o) => [String(o.value), o])).values());

  const normalizeTagOpt = (opt) => {
    const rawLabel = String(opt?.label ?? opt?.value ?? "").trim();
    const slug = createSlug(rawLabel);
    if (!slug) return null;
    return { label: rawLabel, value: slug };
  };

  const [resetKey, setResetKey] = useState(0);
  const isResettingRef = useRef(false);

  // Hydrate editor when server has a head version
  const hydrateFromHead = (content) => {
    if (isResettingRef.current) return;
    if (!currentArticleId) return; // don't hydrate if we're in "new draft" state

    const q = quillRef.current?.getEditor?.();
    if (!q) return;
    if (content && typeof content === "object" && Array.isArray(content.ops)) {
      q.setContents(content, "silent");
    } else if (typeof content === "string") {
      q.clipboard.dangerouslyPasteHTML(content);
    }
  };

  const {
    isSaving,
    lastCommitAt,
    headVersionId,
    lastManual,
    autosaves,
    commitManual,
    commitAutoNow,
    restoreVersion,
  } = useAutoSave({
    quillRef,
    postId: currentArticleId,
    token,
    debounceMs: 5000,
    throttleMs: 10000,
    onLoadedHead: hydrateFromHead,
    onSavedBadge: setSavedBadge,
  });

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const lastSelRef = React.useRef({ index: 0, length: 0 });
  const inProgressRef = React.useRef(false);

  // ---- Smart polling ----
  const pollRef = useRef(null);
  const inFlightPollRef = useRef(false);
  const lastPollAtRef = useRef(0);

  // Prevent clobbering local edits while user is typing
  const lastLocalEditAtRef = useRef(0);

  useEffect(() => {
    const q = quillRef.current?.getEditor?.();
    if (!q) return;

    const onSel = (range) => {
      if (range && typeof range.index === "number") {
        lastSelRef.current = { index: range.index, length: range.length || 0 };
      }
    };

    q.on("selection-change", onSel);
    return () => q.off("selection-change", onSel);
  }, [focusMode, resetKey]);

  useEffect(() => {
    const q = quillRef.current?.getEditor?.();
    if (!q) return;

    const onMouseDown = (e) => {
      const root = q.root;
      if (!root.contains(e.target)) return;

      // Find the blot under the cursor
      const blot = Quill.find(e.target, true);
      if (!blot) return;

      // Convert blot to absolute editor index
      const index = q.getIndex(blot);
      if (typeof index !== "number") return;

      // Only react if we’re inside a suggestion
      const r = getSuggestionRangeAtIndex(q, index);
      if (!r) return;

      // Prevent browser selection drift; select the whole suggestion
      e.preventDefault();
      q.setSelection(r.index, r.length, "silent");
      lastSelRef.current = { index: r.index, length: r.length };
    };

    // Use mousedown so selection is armed before the user releases the click
    q.root.addEventListener("mousedown", onMouseDown);
    return () => q.root.removeEventListener("mousedown", onMouseDown);
  }, [focusMode, resetKey]);

  useEffect(() => {
    let cancelled = false;
    let cleanup = null;
    let retryTimer = null;
    let quillInstance = null;

    const attach = () => {
      if (cancelled) return;

      const q = quillRef.current?.getEditor?.();
      if (!q) {
        retryTimer = setTimeout(attach, 40);
        return;
      }
     if (quillInstance === q) return;
     if (cleanup) cleanup();
     quillInstance = q;

      const history = q.getModule?.("history");
      if (!history) return;

      const update = () => {
        const u = history.stack?.undo || [];
        const r = history.stack?.redo || [];
        setCanUndo(u.length > 0);
        setCanRedo(r.length > 0);
      };

      update();
      q.on("text-change", update);

      cleanup = () => q.off("text-change", update);
    };

    attach();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (cleanup) cleanup();
    };
  }, [focusMode, resetKey]);

  const hasPendingInlineSelRef = useRef(false);
  const lastSelectionRef = useRef(null); // persists selection across focus changes

  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [url, setUrl] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [postSlug, setPostSlug] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [focusKeyphrase, setFocusKeyphrase] = useState("");
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [editors, setEditors] = useState([]);
  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [stage, setStage] = useState("IDEATION");
  const SCHEDULE_PUBLISH_STAGE = "SCHEDULE_PUBLISH";  // Enum key for the “Schedule/Publish” stage from STAGE_ALIASES/STAGE_ORDER
  const [articles, setArticles] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [visibleTooltip, setVisibleTooltip] = useState(null);
  const [mainAuthor, setMainAuthor] = useState("");
  const [editor, setEditor] = useState(null);
  const [estWordCount, setEstWordCount] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [hasPendingInlineSel, setHasPendingInlineSel] = useState(false);
  const [pendingQuote, setPendingQuote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // "draft" or "article"
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [createdAt, setCreatedAt] = useState(null);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [desc, setDesc] = useState("");
  const [status, setStatus] = useState("draft");
  const [finalDraftDue, setFinalDraftDue] = useState(null);
  const [publishedAt, setPublishedAt] = useState(null);
  const [scheduledAt, setScheduledAt] = useState(null);
  const [seoCriticalIssues, setSeoCriticalIssues] = useState([]);

  const location = useLocation();

  const [targetCommentId, setTargetCommentId] = useState(null);
  const [targetCommentType, setTargetCommentType] = useState(null);

  const [mentionUsers, setMentionUsers] = useState([]);

  const params = new URLSearchParams(location.search);
  const draftSlug = params.get("draft");
  const postIdParam = params.get("postId");

  const [showImageModal, setShowImageModal] = useState(false);
  const [editingImageData, setEditingImageData] = useState(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null);

  const [editorHtml, setEditorHtml] = useState("");

  const editorHtmlRef = useRef("");
  useEffect(() => {
    editorHtmlRef.current = editorHtml || "";
  }, [editorHtml]);

  const [activeDraft, setActiveDraft] = useState(null);

  const isWriter = role === 2;
  const isEditor = role === 3;
  const isLeadOrAdmin = role >= 4;

  const canAttemptDelete = Boolean(activeDraft?._id || currentArticleId);

  const normalizeDraftIdsForDelete = (d) => {
    if (!d) return null;

    return {
      ...d,

      // Ensure both "author" id forms exist if one is present
      authorId: d.authorId ?? d.authorMongoId ?? d.author?.userId ?? d.author?._id,
      authorMongoId: d.authorMongoId ?? d.authorId ?? d.author?._id ?? d.author?.userId,

      // Ensure both "editor" id forms exist if one is present
      editorId: d.editorId ?? d.editorMongoId ?? d.editor?._id ?? d.editor?.userId,
      editorMongoId: d.editorMongoId ?? d.editorId ?? d.editor?._id ?? d.editor?.userId,
    };
  };

  // Stage ownership
  const isWriterStage = WRITER_STAGES.has(stage);
  const isEditorStage = EDITOR_STAGES.has(stage);
  const isLeadStage = LEAD_EDITOR_STAGES.has(stage);

  const isNewDraft = !currentArticleId;

  // "Editor stage" for locking writers = editor stages + lead editor stages
  const isAnyEditorOwnedStage = isEditorStage || isLeadStage;

  // Normalize IDs and allow same-user or role ≥ 5 (Lead Editor/Admin) to edit
  const uidCandidates = [user?._id, user?.id, user?.userId].filter(Boolean).map(String);
  const authorIdCandidates = [activeDraft?.authorId, activeDraft?.authorMongoId].filter(Boolean).map(String);
  const editorIdCandidates = [activeDraft?.editorId, activeDraft?.editorMongoId].filter(Boolean).map(String);

  const isAssignedWriter = uidCandidates.some(u => authorIdCandidates.includes(u));
  const isAssignedEditor = uidCandidates.some(u => editorIdCandidates.includes(u));

  const override = role >= 5;

  const canEditWriterNotes = !!activeDraft?._id && (isAssignedWriter || override);
  const canEditEditorNotes = !!activeDraft?._id && (isAssignedEditor || override);

  // Can edit the draft *content + metadata*?
  const canEditDraft =
    isLeadOrAdmin ||
    (isNewDraft && role >= 2) || // allow writer/editor to start a draft
    (isWriter && isWriterStage && isAssignedWriter) ||
    (isEditor && isEditorStage && isAssignedEditor); // editors do NOT edit lead stages

  const isReadOnlyDraft = !canEditDraft;

  // Can submit stage (advance to next stage)?
  const canStageSubmit =
    isLeadOrAdmin ||
    (isNewDraft && isWriter && isWriterStage) || // allow writer to submit an idea pitch
    (isWriter && isWriterStage && isAssignedWriter) ||
    (isEditor && isEditorStage && isAssignedEditor); // writers can't submit editor stages, editors can't submit writer stages

  const getSubmitLabelForStage = (stageKey) => STAGE_SUBMIT_LABELS[stageKey] || "Submit";

  const handleStageSubmit = async () => {
    if (!canStageSubmit) {
      toast.error("This draft is not in your stage. You can add comments and notes, but you can’t submit changes right now.");
      return;
    }
    if (!canEditDraft) {
      toast.error("This draft is locked for editing in the current stage. Comments and notes only.");
      return;
    }
    // Same minimal requirements as saving a draft
    if (!validateSaveFields()) return;

    setIsLoading(true);

    const prevStage = stage;

    try {
      // Hard block: writers/editors cannot schedule drafts
      if (!canSchedule && scheduledDate) {
        setScheduledDate(null);
      }

      // Compute next stage, but don't update UI yet
      const newStage = nextStage(prevStage);

      const authorId = await getMainAuthor();
      const editorId = editor?.value;
      const categories = await getCategories();
      const tags = await getTags();

      const quillHtml = quillRef.current?.getEditor()?.root?.innerHTML || "";

      // Sanitize images (same behaviour as Save as Draft)
      const sanitizedHtml = sanitizeImages(quillHtml);

      if (sanitizedHtml !== quillHtml) {
        toast.warn(
          "One or more unauthorized images were removed and won't be saved. To add images, please use the image upload button."
        );
      }

      const normalizedHtml = normalizeBodyHeadings(sanitizedHtml);

      if (normalizedHtml !== sanitizedHtml) {
        toast.info("We converted H1 headings to H2. Use H2 for section headings—your article title is above.");
      }

      const plainDesc = extractText(normalizedHtml);

      const draftPayload = {
        authorId,
        authors: selectedAuthors,
        editorId,
        stage: newStage,
        estWordCount: estWordCount?.value || "",
        title,
        shortDesc:
          plainDesc.slice(0, 150) + (plainDesc.length > 150 ? "..." : ""),
        desc: normalizedHtml,
        categories,
        tags,
        seoTitle,
        metaDescription,
        focusKeyphrase,
        url,
        status: "draft",
        published: false,
        post_slug: postSlug,
        scheduledAt:
          (canSchedule && status === "draft" && scheduledDate) ? new Date(scheduledDate) : null,
        finalDraftDue: finalDraftDue || null,
      };

      // Include featured image metadata if present
      const featuredImagePayload = getFeaturedImagePayload(featuredImageData);
      if (featuredImagePayload) {
        Object.assign(draftPayload, featuredImagePayload);
      }

      let id = currentArticleId;

      if (isUpdating && currentArticleId) {
        // Update existing draft
        await axios.put(`/posts/${currentArticleId}`, draftPayload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Record a manual version for version history
        await commitManual();

        // Only now update UI stage
        setStage(newStage);
      } else {
        // Create a new draft first (auto-save)
        const res = await axios.post(`/posts`, draftPayload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        id = res.data._id;
        setIsUpdating(true);
        setCurrentArticleId(id);

        // Seed first version in version history
        const q = quillRef.current?.getEditor?.();
        const delta = q?.getContents();
        const content = (delta && delta.ops?.length) ? delta : normalizedHtml;

        await axios.post(
          `/posts/${id}/commit`,
          { type: "manual", content },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Initialize activeDraft
        setActiveDraft({
          _id: id,
          comments: [],
          inlineComments: [],
          writerNotes: res.data.writerNotes || "",
          editorNotes: res.data.editorNotes || "",
          authorId: res.data.authorId,
          editorId: res.data.editorId,
          createdAt: res.data.createdAt || null,
        });

        // Only now update UI stage
        setStage(newStage);
      }

      // Keep local drafts + activeDraft in sync with new stage
      if (id) {
        setDrafts(prev =>
          prev.map(d =>
            d._id === id ? { ...d, stage: newStage } : d
          )
        );

        setActiveDraft(prev =>
          prev && prev._id === id
            ? { ...prev, stage: newStage }
            : prev
        );
      }

      // Optionally refresh lists from backend
      await loadArticles();

      toast.success("Draft successfully submitted");
    } catch (err) {
      setStage(prevStage); // Guarantees no drift
      console.error("Error submitting stage", err);
      toast.error(err?.response?.data?.message || "Error submitting stage");
    } finally {
      setIsLoading(false);
    }
  };

  const getStageBanner = () => {
    if (role >= 4) return null; // Leads/admins see no banner

    const stageName = toHuman(stage);

    // Writer opening an editor-owned stage
    if (isWriter && (isEditorStage || isLeadStage)) {
      return (
        <>
          This draft is in the <strong>{stageName}</strong> stage. Updates are restricted to comments and notes only.
        </>
      );
    }

    // Editor opening a writer-owned stage
    if (isEditor && (isWriterStage || isLeadStage)) {
      return (
        <>
          This draft is in the <strong>{stageName}</strong> stage. Updates are restricted to comments and notes only.
        </>
      );
    }

    return null; // No banner needed
  };

  const [hasInlineFeedback, setHasInlineFeedback] = useState(false);

  const [showFeatureImageModal, setShowFeatureImageModal] = useState(false);
  const [featuredImageData, setFeaturedImageData] = useState(null); // { src, variants, filenameBase, alt, attribution, caption }

  const [suggestionMode, setSuggestionMode] = useState(false);
  const suggestionModeRef = useRef(false);

  const [inlineComments, setInlineComments] = useState([]);

  const [activeInlineCommentId, setActiveInlineCommentId] = useState(null);

  const hasCriticalSeo = seoCriticalIssues.length > 0;

  const onAcceptPointerDown = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inProgressRef.current) return;
    inProgressRef.current = true;

    const q = quillRef.current?.getEditor?.();
    if (q) acceptSuggestionRange(q, null, lastSelRef.current?.index);

    setTimeout(() => { inProgressRef.current = false; }, 0);
  }, []);

  const onRejectPointerDown = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inProgressRef.current) return;
    inProgressRef.current = true;

    const q = quillRef.current?.getEditor?.();
    if (q) rejectSuggestionRange(q, null, lastSelRef.current?.index);

    setTimeout(() => { inProgressRef.current = false; }, 0);
  }, []);

  const previousArticlesMemo = React.useMemo(
    () => [...articles, ...drafts],
    [articles, drafts]
  );

  const handleUndo = () => {
    const q = quillRef.current?.getEditor?.();
    const history = q?.getModule?.("history");
    history?.undo();
  };

  const handleRedo = () => {
    const q = quillRef.current?.getEditor?.();
    const history = q?.getModule?.("history");
    history?.redo();
  };

  const handleGrammarIssueClick = (issue) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill || !issue) return;

    // Use the positions we now send from the backend
    const start = issue.start ?? issue.location?.start ?? 0;
    const length =
      issue.length ??
      (issue.location?.end != null && issue.location?.start != null
        ? issue.location.end - issue.location.start
        : 1);

    // Focus editor and select offending text
    quill.focus();
    quill.setSelection(start, length, "user");

    const bounds = quill.getBounds(start, length);
    if (!bounds) return;

    // 1) Center the selection inside Quill's own scrolling container
    const scroller = quill.scrollingContainer || quill.root.parentElement;
    if (scroller) {
      const targetScrollTop =
        bounds.top + bounds.height / 2 - scroller.clientHeight / 2;

      scroller.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: "smooth",
      });
    }

    // 2) Center the editor block itself in the browser viewport
    const editorRoot = quill.root;
    const editorRect = editorRoot.getBoundingClientRect();

    const currentScrollTop =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const targetPageScrollTop =
      currentScrollTop +
      editorRect.top +
      editorRect.height / 2 -
      window.innerHeight / 2;

    window.scrollTo({
      top: Math.max(0, targetPageScrollTop),
      behavior: "smooth",
    });
  };

  // When Est. Word Count changes, update both estWordCount and finalDraftDue
  const handleEstWordCountChange = (opt) => {
    // `opt` is typically { value, label } or null
    setEstWordCount(opt || null);

    if (!opt || !opt.value) {
      setFinalDraftDue(null);
      return;
    }

    const nextDue = computeFinalDraftDue(opt.value, activeDraft?.createdAt);
    setFinalDraftDue(nextDue);
  };

  // install Suggestion Mode listeners when the editor is actually ready
  useEffect(() => {
    let cleanup = () => {};
    let cancelled = false;

    const init = () => {
      const quill = quillRef.current?.getEditor?.();
      if (!quill) {
        if (!cancelled) setTimeout(init, 25); // retry until ReactQuill mounts
        return;
      }

      if (quill.__suggestionModeInstalled) return;
      quill.__suggestionModeInstalled = true;

      // --- handlers ---
      const onTextChange = (delta, _old, source) => {
        if (!suggestionModeRef.current || source !== "user") return;

        let pos = 0;
        let insertStart = null;
        let insertTotal = 0;

        (delta.ops || []).forEach(op => {
          if (typeof op.retain === "number") {
            // If we hit a retain and we already have an accumulated insert run, flush it.
            if (insertStart !== null && insertTotal > 0) {
              quill.formatText(insertStart, insertTotal, "suggest-insert", "1", "silent");
              insertStart = null;
              insertTotal = 0;
            }
            pos += op.retain;
            return;
          }

          if (op.insert) {

            if (op.attributes?.["suggest-delete"]) {

              if (insertStart !== null && insertTotal > 0) {
                quill.formatText(insertStart, insertTotal, "suggest-insert", "1", "silent");
                insertStart = null;
                insertTotal = 0;
              }

              const len = typeof op.insert === "string" ? op.insert.length : 1;
              pos += len;
              return;
            }

            const len = typeof op.insert === "string" ? op.insert.length : 1;
            if (insertStart === null) insertStart = pos;
            insertTotal += len;
            pos += len;
            return;
          }

          // deletes don't need formatting; just ensure any pending run is flushed later
        });

        // Flush any trailing insert run once
        if (insertStart !== null && insertTotal > 0) {
          quill.formatText(insertStart, insertTotal, "suggest-insert", "1", "silent");
        }
      };

      const backspaceBinding = quill.keyboard.addBinding({ key: "backspace" }, (range) => {
        if (!suggestionModeRef.current) return true;
        if (range.length > 0) {
          const txt = quill.getText(range.index, range.length);
          quill.deleteText(range.index, range.length, "silent");
          quill.insertText(range.index, txt, { "suggest-delete": "1" }, "user");
          quill.setSelection(range.index, 0, "silent");
          return false;
        }
        if (range.index > 0) {
          const txt = quill.getText(range.index - 1, 1);
          if (txt && txt !== "\n") {
            quill.deleteText(range.index - 1, 1, "silent");
            quill.insertText(range.index - 1, txt, { "suggest-delete": "1" }, "user");
            quill.setSelection(range.index - 1, 0, "silent");
          }
          return false;
        }
        return false;
      });

      const deleteBinding = quill.keyboard.addBinding({ key: "delete" }, (range) => {
        if (!suggestionModeRef.current) return true;
        if (range.length > 0) {
          const txt = quill.getText(range.index, range.length);
          quill.deleteText(range.index, range.length, "silent");
          quill.insertText(range.index, txt, { "suggest-delete": "1" }, "user");
          quill.setSelection(range.index, 0, "silent");
          return false;
        }
        const txt = quill.getText(range.index, 1);
        if (txt && txt !== "\n") {
          quill.deleteText(range.index, 1, "silent");
          quill.insertText(range.index, txt, { "suggest-delete": "1" }, "user");
          quill.setSelection(range.index, 0, "silent");
        }
        return false;
      });

      quill.on("text-change", onTextChange);

      // cleanup for unmount/remount
      cleanup = () => {
        quill.off("text-change", onTextChange);
        // keyboard bindings are gated by suggestionModeRef, so no hard removal needed
      };
    };

    init();
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [focusMode, resetKey]);

  useEffect(() => {
    if (!currentArticleId) return;
    axios.get(`/posts/${currentArticleId}/inlineComments`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(({data}) => setInlineComments(data))
      .catch(() => setInlineComments([]));
  }, [currentArticleId, token]);

  useEffect(() => {
    hasPendingInlineSelRef.current = hasPendingInlineSel;
  }, [hasPendingInlineSel]);

  const primeInlineComment = () => {
    const quill = quillRef.current?.getEditor();
    if (!currentArticleId) return toast.error("Save the draft first.");
    if (!quill) return;

    // Prefer current selection; otherwise use the cached one from onSelectionChange
    const live = quill.getSelection();
    const sel = (live && live.length > 0) ? live : lastSelectionRef.current;

    if (!sel || sel.length === 0) {
      toast.error("Select some text in the editor first.");
      lastSelectionRef.current = null;
      setHasPendingInlineSel(false);
      setPendingQuote("");
      return;
    }

    lastSelectionRef.current = sel;      // cache it
    setHasPendingInlineSel(true);        // mark that a selection is waiting
    setPendingQuote(quill.getText(sel.index, sel.length).trim());
    toast.info("Type your comment in the Inline Comments panel, then click Add Comment.");
  };

  // --- Inline comment markers (minimal, backward-compatible) ---
  const mkId = () => {
    try { return crypto.randomUUID(); } catch (_) {}
    return `m_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  };

  // Find the absolute index of an embed marker by markerId by walking the Delta.
  const findMarkerIndex = (quill, markerId) => {
    if (!quill || !markerId) return null;
    const d = quill.getContents();
    let pos = 0;
    for (const op of d.ops || []) {
      if (op.insert && typeof op.insert === "object") {
        // Support either { inlineCommentStart: { markerId } } or { inlineCommentEnd: { markerId } }
        const key = Object.keys(op.insert)[0];
        const val = op.insert[key];
        if ((key === "inlineCommentStart" || key === "inlineCommentEnd") && val?.markerId === markerId) {
          return pos;
        }
        // embeds are length 1
        pos += 1;
        continue;
      }
      if (typeof op.insert === "string") {
        pos += op.insert.length;
        continue;
      }
      if (typeof op.retain === "number") pos += op.retain;
    }
    return null;
  };

  // Compute the *current* highlight range for a comment.
  // Marker-first; fallback to stored rangeIndex/rangeLength.
  const getLiveCommentRange = (quill, comment) => {
    if (!quill || !comment) return null;

    const startId = comment.startMarkerId || comment.startMarker?.markerId;
    const endId = comment.endMarkerId || comment.endMarker?.markerId;

    if (startId && endId) {
      const s = findMarkerIndex(quill, startId);
      const e = findMarkerIndex(quill, endId);
      if (typeof s === "number" && typeof e === "number" && e > s) {
        // markers themselves occupy 1 char each; highlight between them
        const index = s + 1;
        const length = Math.max(0, e - s - 1);
        if (length > 0) return { index, length };
      }
    }

    // legacy fallback
    if (typeof comment.rangeIndex === "number" && typeof comment.rangeLength === "number" && comment.rangeLength > 0) {
      return { index: comment.rangeIndex, length: comment.rangeLength };
    }

    return null;
  };

  const addInlineComment = async (...args) => {
    let draftId, text, quoteFromChild;
    if (args.length === 1 && args[0] && typeof args[0] === "object") {
      ({ draftId, text, quoteText: quoteFromChild } = args[0]);
    } else {
      [draftId, text] = args;
    }

    if (!draftId) draftId = activeDraft?._id || currentArticleId;

    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    // Try current selection; fall back to frozen selection
    const sel = quill.getSelection(true) || lastSelectionRef.current;
    if (!sel || sel.length === 0) {
      toast.error("Select some text in the editor first.");
      return;
    }

    // Require comment text (from the panel)
    const clean = (text || "").trim();
    if (!clean) {
      toast.error("Type your comment in the Inline Comments panel.");
      return;
    }

    const quoteText =
      (quoteFromChild || "").trim() ||
      quill.getText(sel.index, sel.length).trim();

    if (!quoteText) {
      toast.error("Could not read the selected text.");
      return;
    }

    const rangeIndex = sel.index;
    const rangeLength = sel.length;

    // Marker IDs (new system). We generate IDs client-side so we can insert embeds immediately,
    // and persist them on the server alongside the comment.
    const startMarkerId = mkId();
    const endMarkerId = mkId();

    // Try inserting marker embeds if the blots are registered.
    // If not registered yet, this silently fails and we remain legacy-only.
    try {
      // Insert end marker first so indices don't shift for start insertion
      quill.insertEmbed(sel.index + sel.length, "inlineCommentEnd", { markerId: endMarkerId }, "silent");
      quill.insertEmbed(sel.index, "inlineCommentStart", { markerId: startMarkerId }, "silent");
    } catch (_) {
      // No-op: fallback to range-only drafts until blots exist everywhere
    }

    // Optimistic update: update activeDraft and drafts so the panel reflects the new comment
    const optimistic = {
      _id: `temp_${Date.now()}`,
      authorName: user?.user_name || user?.username || "You",
      role: user?.role,
      text: clean,
      quoteText,
      rangeIndex,
      rangeLength,
      startMarkerId,
      endMarkerId,
      timestamp: new Date().toISOString(),
    };

    setActiveDraft(prev =>
      prev && prev._id === draftId
        ? { ...prev, inlineComments: [optimistic, ...(prev.inlineComments || [])] }
        : prev
    );

    setDrafts(prev =>
      prev.map(d =>
        d._id === draftId
          ? { ...d, inlineComments: [optimistic, ...(d.inlineComments || [])] }
          : d
      )
    );

    try {
      const { data } = await axios.post(
        `/posts/${draftId}/inlineComments`,
        { text: clean, quoteText, rangeIndex, rangeLength, startMarkerId, endMarkerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Replace optimistic with server result
      setActiveDraft(prev =>
        prev && prev._id === draftId
          ? {
              ...prev,
              inlineComments: (prev.inlineComments || []).map(c =>
                c._id === optimistic._id ? data : c
              ),
            }
          : prev
      );

      setDrafts(prev =>
        prev.map(d =>
          d._id === draftId
            ? {
                ...d,
                inlineComments: (d.inlineComments || []).map(c =>
                  c._id === optimistic._id ? data : c
                ),
              }
            : d
        )
      );

      // Clear the cached selection once comment is successfully saved
      lastSelectionRef.current = null;
      setHasPendingInlineSel(false);
      setPendingQuote("");
    } catch (e) {

      // Roll back optimistic on failure
      setActiveDraft(prev =>
        prev && prev._id === draftId
          ? {
              ...prev,
              inlineComments: (prev.inlineComments || []).filter(c => c._id !== optimistic._id),
            }
          : prev
      );

      setDrafts(prev =>
        prev.map(d =>
          d._id === draftId
            ? {
                ...d,
                inlineComments: (d.inlineComments || []).filter(c => c._id !== optimistic._id),
              }
            : d
        )
      );
      toast.error(e?.response?.data?.message || "Failed to add inline comment");
    }
  };

  const clearInlineSelection = () => {
    lastSelectionRef.current = null;
    setHasPendingInlineSel(false);
    setPendingQuote("");
  };

  // Central helper so we can reapply inline comment highlights on demand
  const applyInlineCommentHighlights = useCallback((quill, inlineComments, activeId) => {

    if (!quill || !inlineComments) return;

    const length = quill.getLength();

    // Clear all existing inlineComment formatting
    quill.formatText(0, length, "inlineComment", false, "silent");

    // Reapply for each stored inline comment (marker-first, legacy fallback)
    inlineComments.forEach((c) => {
      const r = getLiveCommentRange(quill, c);
      if (!r) return;
      quill.formatText(
        r.index,
        r.length,
        "inlineComment",
        { id: c._id, active: c._id === activeId },
        "silent"
      );
    });
  }, []);

  useEffect(() => {

    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;

    applyInlineCommentHighlights(
      quill,
      activeDraft?.inlineComments || [],
      activeInlineCommentId
    );
  }, [activeDraft?.inlineComments, activeInlineCommentId, applyInlineCommentHighlights]);

  useEffect(() => {
    let cancelled = false;
    let cleanup = null;

    const attach = () => {
      if (cancelled) return;

      const quill = quillRef.current?.getEditor?.();
      if (!quill) {
        // ReactQuill might not be ready on first effect run; retry
        setTimeout(attach, 30);
        return;
      }

      const root = quill.root;

      const handleClick = (event) => {
        if (!root.contains(event.target)) return;

        // Find the blot under the click
        const blot = Quill.find(event.target, true);
        if (!blot) return;

        // Convert that blot to an absolute index in the doc
        const index = quill.getIndex(blot);
        if (typeof index !== "number") return;

        const comments = activeDraft?.inlineComments || [];

        // Find which inline comment range this index falls into (marker-first)
        const hit = comments.find((c) => {
          const r = getLiveCommentRange(quill, c);
          return r && index >= r.index && index < r.index + r.length;
        });

        if (hit && hit._id) {
          setActiveInlineCommentId(hit._id);
        }
      };

      root.addEventListener("click", handleClick);
      cleanup = () => root.removeEventListener("click", handleClick);
    };

    attach();

    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, [activeDraft?.inlineComments, focusMode, resetKey]);

  const handleSelectInlineComment = (comment) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) {
      setActiveInlineCommentId(comment._id);
      return;
    }

    const r = getLiveCommentRange(quill, comment);
    if (r) {
      quill.focus();
      quill.setSelection(r.index, r.length, "user");
    }

    // Force a repaint even if the same comment is selected again
    applyInlineCommentHighlights(
      quill,
      activeDraft?.inlineComments || [],
      comment._id
    );

    setActiveInlineCommentId(comment._id);
  };

  useEffect(() => {
    // reset active inline comment when the draft changes
    setActiveInlineCommentId(null);
  }, [activeDraft?._id]);

  const saveWriterNotes = async (postId, text) => {
    await axios.patch(`/posts/${postId}`, { writerNotes: text }, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 8000 }
    );
    // keep activeDraft in sync (optional)
    setActiveDraft(prev => prev && prev._id === postId ? { ...prev, writerNotes: text } : prev);
  };

  const saveEditorNotes = async (postId, text) => {
    await axios.patch(`/posts/${postId}`, { editorNotes: text }, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 8000 }
    );
    setActiveDraft(prev => prev && prev._id === postId ? { ...prev, editorNotes: text } : prev);
  };

  const addComment = async (draftId, text) => {
    const res = await axios.post(
      `/posts/${draftId}/comments`,
      { text },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const newComment = res.data;

    // update activeDraft (modal) if it’s the same draft
    setActiveDraft(prev =>
      prev && prev._id === draftId
        ? { ...prev, comments: [...(prev.comments || []), newComment] }
        : prev
    );

    // also update in the main list so it stays in sync
    setDrafts(prev =>
      prev.map(d =>
        d._id === draftId
          ? { ...d, comments: [...(d.comments || []), newComment] }
          : d
      )
    );
  };

  const deleteComment = async (draftId, commentId) => {
    // optimistic UI
    setActiveDraft(prev =>
      prev && prev._id === draftId
        ? { ...prev, comments: (prev.comments || []).filter(c => c._id !== commentId) }
        : prev
    );
    setDrafts(prev =>
      prev.map(d => d._id === draftId
        ? { ...d, comments: (d.comments || []).filter(c => c._id !== commentId) }
        : d)
    );

    try {
      await axios.delete(`/posts/${draftId}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      // rollback if server fails
      await loadArticles();
      console.error("Delete comment failed", e);
      toast?.error?.("Couldn't delete comment.");
    }
  };

  const deleteInlineComment = async (draftId, inlineCommentId) => {
    const commentToDelete =
      (activeDraft?.inlineComments || []).find(x => x._id === inlineCommentId) ||
      (drafts.find(d => String(d._id) === String(draftId))?.inlineComments || []).find(x => x._id === inlineCommentId);

    // optimistic UI
    setActiveDraft(prev =>
      prev && prev._id === draftId
        ? { ...prev, inlineComments: (prev.inlineComments || []).filter(c => c._id !== inlineCommentId) }
        : prev
    );
    setDrafts(prev =>
      prev.map(d => d._id === draftId
        ? { ...d, inlineComments: (d.inlineComments || []).filter(c => c._id !== inlineCommentId) }
        : d)
    );

    try {
      const quill = quillRef.current?.getEditor?.();
      if (quill) {
        const sId = commentToDelete?.startMarkerId;
        const eId = commentToDelete?.endMarkerId;

        const s = findMarkerIndex(quill, sId);
        const e = findMarkerIndex(quill, eId);
        if (typeof e === "number") quill.deleteText(e, 1, "silent");
        if (typeof s === "number") quill.deleteText(s, 1, "silent");
      }
    } catch (_) {}

    try {
      await axios.delete(`/posts/${draftId}/inlineComments/${inlineCommentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      // rollback if server fails
      await loadArticles();
      console.error("Delete inline comment failed", e);
      toast?.error?.("Couldn't delete inline comment.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    let quillInstance = null;
    let cleanup = null;
    let retryTimer = null;

    const MAX_ATTEMPTS = 100;      // ~3s at 30ms per attempt
    const RETRY_DELAY_MS = 30;

    const attach = (attempt = 0) => {
      if (cancelled) return;

      const quill = quillRef.current?.getEditor?.();
      if (!quill) {
        // ReactQuill not ready yet; try again shortly
        if (attempt >= MAX_ATTEMPTS) {
          console.error(
            "[Write] Quill editor not ready after max attempts; word count listeners not attached."
          );
          return;
        }
        retryTimer = setTimeout(() => attach(attempt + 1), RETRY_DELAY_MS);
        return;
      }

      // If we already attached to this same instance, do nothing
      if (quillInstance === quill) return;

      // If we’re switching instances within the same effect run, cleanup old listeners
      if (cleanup) cleanup();

      quillInstance = quill;

      const onSelectionChange = (range) => {
        if (range && range.length > 0) {
          lastSelectionRef.current = range;
        }
      };

      const updateHtml = () => {
        const html = quill.root.innerHTML;
        if (editorHtmlRef.current === html) return;

        lastLocalEditAtRef.current = Date.now();

        const text = quill.getText();
        const trimmed = text.replace(/\s+/g, " ").trim();
        const count = trimmed ? trimmed.split(" ").length : 0;

        setEditorHtml(html);
        setWordCount(count);
      };

      // Initialize once with current content
      updateHtml();

      quill.on("selection-change", onSelectionChange);
      quill.on("text-change", updateHtml);

      // Proper cleanup
      cleanup = () => {
        quill.off("selection-change", onSelectionChange);
        quill.off("text-change", updateHtml);
      };
    };

    attach();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (cleanup) cleanup();
    };
  }, [focusMode, resetKey]);

  // Re-hydrate Quill when focus mode toggles (ReactQuill remounts)
  useEffect(() => {
    if (isResettingRef.current) return;

    let cancelled = false;

    const hydrate = () => {
      if (cancelled) return;

      const q = quillRef.current?.getEditor?.();
      if (!q) {
        setTimeout(hydrate, 30);
        return;
      }

      const html = editorHtmlRef.current || "";
      const delta = q.clipboard.convert(html || "<p><br></p>");
      q.setContents(delta, "silent");
    };

    hydrate();
    return () => { cancelled = true; };
  }, [focusMode, resetKey]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const commentId = params.get("commentId");
    const commentType = params.get("commentType");

    if (commentId) {
      setTargetCommentId(commentId);
      setTargetCommentType(
        commentType === "INLINE" ? "INLINE" : "COMMENT"
      );
    } else {
      setTargetCommentId(null);
      setTargetCommentType(null);
    }
  }, [location.search]);

  useEffect(() => {
    if (!targetCommentId) return;

    const domId =
      targetCommentType === "INLINE"
        ? `inline-comment-${targetCommentId}`
        : `comment-${targetCommentId}`;

    const el = document.getElementById(domId);
    if (!el) return;

    // Scroll into view
    el.scrollIntoView({ behavior: "smooth", block: "center" });

    // Add a temporary highlight class
    el.classList.add("comment-highlight");
    const timeout = setTimeout(() => {
      el.classList.remove("comment-highlight");
    }, 2000);

    return () => clearTimeout(timeout);
  }, [
    targetCommentId,
    targetCommentType,
    activeDraft?.comments?.length,
    activeDraft?.inlineComments?.length,
  ]);

  useEffect(() => {
    if (!token) return;

    axios
      .get("/users/mentionable", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setMentionUsers(res.data || []);
      })
      .catch((err) => {
        console.error("Failed to load mentionable users:", err);
      });
  }, [token]);

  const loadArticles = useCallback(async () => {
    try {
      const url =
        user.role >= 4
          ? "/posts?all=true" // Admins get all posts
          : "/posts";          // Others get filtered set

      const articlesRes = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const allArticles = articlesRes.data;

      // Published articles: filter + sort by publishedAt (desc)
      const publishedArticles = allArticles
        .filter((article) => article.status === "published" && article.publishedAt)
        .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));

      // Drafts (visible based on role)
      const visibleDrafts = allArticles.filter((article) => {
        if (article.status !== "draft") return false;
        if (user.role >= 4) return true; // Admins see all drafts
        if (user.role === 3) {
          const uidCandidates = [user?._id, user?.id, user?.userId].filter(Boolean).map(String);
          const editorCandidates = [article.editorId, article.editorMongoId, article.editor?._id, article.editor?.userId]
            .filter(Boolean).map(String);
          return uidCandidates.some(u => editorCandidates.includes(u));
        }
        if (user.role === 2) {
          const uidCandidates = [user?._id, user?.id, user?.userId].filter(Boolean).map(String);
          const authorCandidates = [article.authorId, article.authorMongoId, article.author?._id, article.author?.userId]
            .filter(Boolean)
            .map(String);
          return uidCandidates.some(u => authorCandidates.includes(u));
        }

        return false; // Readers and unauthorized users
      });

      setArticles(publishedArticles);
      setDrafts(visibleDrafts);

    } catch (err) {
      console.error("Error fetching articles", err);
    }
  }, [user?._id, user?.role, user?.userId, token]);

  const refreshOpenDraftCollab = useCallback(async () => {
    const id = activeDraft?._id || currentArticleId;
    if (!id || !token) return;

    // Don't stomp while user is actively editing content.
    // (This only guards Quill/HTML, not comments/notes.)
    const msSinceEdit = Date.now() - (lastLocalEditAtRef.current || 0);
    const isActivelyTyping = msSinceEdit < 2500; // tweak if you want
    // We still refresh comments/inline comments even if typing.
    // We just avoid re-hydrating editor HTML unless you're already doing that elsewhere.

    const headers = { Authorization: `Bearer ${token}` };

    // Fetch post + comments in parallel, tolerate missing endpoints
    const [postRes, commentsRes, inlineRes] = await Promise.all([
      axios.get(`/posts/id/${encodeURIComponent(id)}`, { headers }),
      axios.get(`/posts/${id}/comments`, { headers }).catch(() => ({ data: null })),
      axios.get(`/posts/${id}/inlineComments`, { headers }).catch(() => ({ data: null })),
    ]);

    const post = postRes.data;

    // Merge into activeDraft without disturbing local UI state
    setActiveDraft((prev) => {
      if (!prev || String(prev._id) !== String(id)) return prev;

      const next = { ...prev };

      // Always refresh collab arrays + notes (safe)
      next.comments = commentsRes.data || post.comments || prev.comments || [];
      next.inlineComments = inlineRes.data || post.inlineComments || prev.inlineComments || [];
      next.writerNotes = post.writerNotes ?? prev.writerNotes ?? "";
      next.editorNotes = post.editorNotes ?? prev.editorNotes ?? "";

      // Keep ids updated if backend normalized them
      next.authorId = post.authorId ?? prev.authorId;
      next.editorId = post.editorId ?? prev.editorId;

      return next;
    });

    // Keep sidebar list in sync too (comment counts etc)
    setDrafts((prev) =>
      prev.map((d) => {
        if (String(d._id) !== String(id)) return d;
        return {
          ...d,
          comments: commentsRes.data || post.comments || d.comments || [],
          inlineComments: inlineRes.data || post.inlineComments || d.inlineComments || [],
          writerNotes: post.writerNotes ?? d.writerNotes,
          editorNotes: post.editorNotes ?? d.editorNotes,
          stage: post.stage ?? d.stage,
          status: post.status ?? d.status,
          scheduledAt: post.scheduledAt ?? d.scheduledAt,
          publishedAt: post.publishedAt ?? d.publishedAt,
        };
      })
    );

    // Keep stage state in sync, but don’t flip the UI while user is actively typing
    if (!isActivelyTyping && post?.stage && post.stage !== stage) {
      setStage(post.stage);
    }

    // IMPORTANT:
    // We intentionally DO NOT overwrite quill content here.
    // Your autosave hook already hydrates from head when appropriate.
  }, [activeDraft?._id, currentArticleId, token, stage, loadArticles]);

  const handleEditDraft = (draft) => {
    if (!draft) return;

    toggleSidebar();

    const slug = pickSlug(draft);
    if (slug) {
      navigate(`/write?draft=${encodeURIComponent(slug)}`);
    } else if (draft._id) {
      navigate(`/write?postId=${encodeURIComponent(draft._id)}`);
    } else {
      toast.error("Unable to open draft: missing slug and ID.");
    }
  };

  const handleEditArticle = (article) => {
    if (!article) return;

    toggleSidebar();

    const slug = pickSlug(article);
    if (slug) {
      navigate(`/write?draft=${encodeURIComponent(slug)}`);
    } else if (article._id) {
      navigate(`/write?postId=${encodeURIComponent(article._id)}`);
    } else {
      toast.error("Unable to open article: missing slug and ID.");
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (user.role < 4) {
      toast.error("You are not authorized to delete articles.");
      return;
    }

    try {
      await axios.delete(`/posts/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setArticles((prev) => prev.filter((article) => article._id !== articleId));

      toast.success("Article deleted successfully!");
    } catch (err) {
      console.error("Error deleting article", err);
      toast.error("Error deleting article");
    }
  };

  const canDeleteDraft = (draftRaw) => {
    const draft = normalizeDraftIdsForDelete(draftRaw);
    if (!draft) return false;

    // Lead/Admin can delete any draft
    if (Number(user?.role) >= 4) return true;

    const uidCandidates = [user?._id, user?.id, user?.userId]
      .filter(Boolean)
      .map(String);

    const authorIds = [
      draft.authorId,
      draft.authorMongoId,
      draft.author?.userId,
      draft.author?._id
    ].filter(Boolean).map(String);

    const editorIds = [
      draft.editorId,
      draft.editorMongoId,
      draft.editor?._id,
      draft.editor?.userId
    ].filter(Boolean).map(String);

    // Writer can delete if they are the assigned writer
    if (Number(user?.role) === 2) {
      return uidCandidates.some((u) => authorIds.includes(u));
    }

    // Editor can delete if they are the assigned editor
    if (Number(user?.role) === 3) {
      return uidCandidates.some((u) => editorIds.includes(u));
    }

    return false;
  };

  const handleDeleteDraft = async (draftId) => {
    const idToDelete = draftId || activeDraft?._id || currentArticleId;
    if (!idToDelete) return;

    // Find the draft from state (so we can check ownership)
    const draft = normalizeDraftIdsForDelete(
      drafts.find((d) => String(d._id) === String(idToDelete)) ||
      (activeDraft && String(activeDraft._id) === String(idToDelete) ? activeDraft : null)
    );

    if (!canDeleteDraft(draft)) {
      toast.error("You are not authorized to delete this draft.");
      return;
    }

    try {
      await axios.delete(`/posts/${idToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDrafts((prev) => prev.filter((d) => String(d._id) !== String(idToDelete)));

      if (String(currentArticleId) === String(idToDelete)) {
        handleCancelDraft();
      }

      toast.success("Draft deleted successfully!");
    } catch (err) {
      console.error("Error deleting draft", err);
      toast.error(err?.response?.data?.message || "Error deleting draft");
    }
  };

  const handleSwitchToPublish = async (draftId) => {
    if (user.role < 4) {
      toast.error("You are not authorized to publish.");
      return;
    }

    try {
      const newArticle = {
        status: "published",
        scheduledAt: null,
      };

      await axios.put(`/posts/${draftId}`, newArticle, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDrafts(drafts.filter((draft) => draft._id !== draftId));
      toast.success("Successfully switched to article!");
      loadArticles();
    } catch (err) {
      console.error("Error switching to article:", err);
      toast.error("Failed to switch to article.");
    }
  };

  const handleSwitchToDraft = async (articleId) => {
    if (user.role < 4) {
      toast.error("You are not authorized to unpublish.");
      return;
    }

    try {
      const newArticle = {
        status: "draft",
        scheduledAt: null,
      };

      await axios.put(`/posts/${articleId}`, newArticle, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setArticles(articles.filter((art) => art._id !== articleId));
      toast.success("Successfully switched to draft!");
      loadArticles();
    } catch (err) {
      console.error("Error switching to draft:", err);
      toast.error("Failed to switch to draft.");
    }
  };

  const getMainAuthor = async () => mainAuthor?.value;

  const getCategories = async () => {
    try {
      const res = await axios.get(`/categories`);
      const categories = res.data.filter((cat) =>
        selectedCategories
          .map((category) => category.value)
          .includes(cat.cat_slug)
      );
      return categories;
    } catch (err) {
      console.error("Error fetching categories!", err);
    }
  };

  const getTags = async () => {
    try {
      const res = await axios.get(`/tags`);
      const tags = res.data.filter((tag) =>
        selectedTags.map((tag) => tag.value).includes(tag.tag_slug)
      );
      return tags;
    } catch (err) {
      console.error("Error fetching tags!", err);
    }
  };

  useEffect(() => {
    if (!user || !token) {
      navigate("/login");
    }

    try {
      const userRole = Number(jwtDecode(token)?.role);
      setIsAuthorized(userRole > 1);
    } catch (err) {
      console.error("Invalid token", err);
      setIsAuthorized(false);
    }
  }, [user, token, navigate]);

  const loadCategoriesAndTags = async () => {
    try {
      const usersRes = await axios.get(`/users`);
      const allUsers = usersRes.data;

      const authors = allUsers.filter((user) => user.isAuthor);
      const editors = allUsers.filter((user) => [3, 4].includes(user.role));

      setAuthors(
        authors.map((user) => ({
          label: decodeHtml(user.user_name),
          value: user.userId,
        }))
      );

      setEditors(
        editors.map((user) => ({
          label: decodeHtml(user.user_name),
          value: user._id,
        }))
      );

      const categoriesRes = await axios.get(`/categories`);
      setCategories(
        categoriesRes.data.map((cat) => ({
          label: decodeHtml(cat.cat_name),
          value: cat.cat_slug,
        }))
      );

      const tagsRes = await axios.get(`/tags`);

      setTags(
        dedupeByValue(
          tagsRes.data.map((tag) => ({
            label: decodeHtml(tag.tag_name),
            value: tag.tag_slug,
          }))
        )
      );

    } catch (err) {
      console.error("Error fetching categories or tags", err);
    }
  };

  {/*
  const loadDraft = async (slug) => {
    try {
      const draftRes = await axios.get(`/posts/${slug}`);
      const draft = draftRes.data;
      setEstWordCount(
        draft.estWordCount
          ? { value: draft.estWordCount, label: draft.estWordCount }
          : null
      );
      setStage(draft.stage || "IDEATION");
      setCreatedAt(draft.createdAt || null);
      setTitle(draft.title);
      setPostSlug(draft.post_slug);
      setSeoTitle(draft.seoTitle || "");
      setMetaDescription(draft.metaDescription || "");
      setFocusKeyphrase(draft.focusKeyphrase || "");
      setUrl(draft.url || "");
      setSelectedCategories(
        draft.categories.map((cat) => ({
          label: decodeHtml(cat.cat_name),
          value: decodeHtml(cat.cat_slug),
        }))
      );
      setSelectedAuthors(draft.authors);
      setSelectedTags(
        draft.tags.map((tag) => ({
          label: decodeHtml(tag.tag_name),
          value: decodeHtml(tag.tag_slug),
        }))
      );
      setCurrentArticleId(draft._id);

      setActiveDraft({
        _id: draft._id,
        comments: draft.comments || [],
        inlineComments: draft.inlineComments || [],
        writerNotes: draft.writerNotes || "",
        editorNotes: draft.editorNotes || "",
        authorId: draft.authorId,
        editorId: draft.editorId,
        createdAt: draft.createdAt || null,
      });

      setIsUpdating(true);

      const author = await axios.get(`/users/${draft.authorId}`);
      setMainAuthor({
        label: author.data.user_name,
        value: author.data.user_slug,
      });

      const editor = await axios.get(`/users/${draft.editorId}`);
      setEditor({
        label: editor.data.user_name,
        value: editor.data.user_slug,
      });

      // Set featured image (supports new + legacy shapes)
      const fiDraft = normalizeFeaturedImageFromPost(draft);
      setFeaturedImageData(fiDraft);
      setCaption(fiDraft?.caption || "");

      setTimeout(() => {
        const quill = quillRef.current?.getEditor();
        if (quill && draft.desc) {
          quill.clipboard.dangerouslyPasteHTML(draft.desc);
          setEditorHtml(draft.desc || "");
        }
      }, 0);

      setFinalDraftDue(draft.finalDraftDue || null);
      setPublishedAt(draft.publishedAt || null);
      setScheduledAt(draft.scheduledAt || null);
      setStatus(draft.status);

    } catch (err) {
      console.error("Error fetching draft", err);
    }
  };
  */}

  useEffect(() => {
    const loadInitialData = async () => {
      await loadCategoriesAndTags();
      await loadArticles();
    };

    loadInitialData();
  }, [loadArticles]);

  useEffect(() => {
    let cancelled = false;

    const loadBySlug = async (slug) => {
      try {
        const { data: article } = await axios.get(
          `/posts/slug/${encodeURIComponent(slug)}?includeDrafts=true`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return article;
      } catch (err) {
        console.error("Error loading by slug:", err?.response?.data || err);
        throw err;
      }
    };

    const loadById = async (id) => {
      try {
        const { data: article } = await axios.get(
          `/posts/id/${encodeURIComponent(id)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return article;
      } catch (err) {
        console.error("Error loading by id:", err?.response?.status, err?.response?.data || err);
        throw err;
      }
    };

    const hydrateStateFromArticle = async (article) => {
      if (cancelled) return;

      if (article?._id && article._id === currentArticleId) {
        // already showing this doc; nothing to do
        return;
      }

      setMainAuthor(
        authors.find((a) => Number(a.value) === Number(article.authorId)) || null
      );
      setEditor(
        editors.find((e) => String(e.value) === String(article.editorId)) || null
      );
      setStage(article.stage || "IDEATION");
      setEstWordCount(
        article.estWordCount
          ? { value: article.estWordCount, label: article.estWordCount }
          : null
      );
      setCreatedAt(article.createdAt || null);
      setTitle(article.title || "");
      setSeoTitle(article.seoTitle || "");
      setMetaDescription(article.metaDescription || "");
      setFocusKeyphrase(article.focusKeyphrase || "");
      setPostSlug(article.post_slug || "");
      setSelectedCategories(
        dedupeByValue(
          (article.categories || [])
            .map((category) => categories.find((c) => c.value === category.cat_slug))
            .filter(Boolean)
        )
      );
      setSelectedTags(
        dedupeByValue(
          (article.tags || [])
            .map((tag) => tags.find((t) => t.value === tag.tag_slug))
            .filter(Boolean)
        )
      );

      const fiArticle = normalizeFeaturedImageFromPost(article);
      setFeaturedImageData(fiArticle);
      setCaption(fiArticle?.caption || "");

      setDesc(article.desc || "");

      const quill = quillRef.current?.getEditor?.();
      if (quill) {
        const delta = quill.clipboard.convert(article.desc || "<p><br></p>");
        quill.setContents(delta, "silent");
      }

      setEditorHtml(article.desc || "");

      setCurrentArticleId(article._id);

      setActiveDraft({
        _id: article._id,
        comments: article.comments || [],
        inlineComments: article.inlineComments || [],
        writerNotes: article.writerNotes || "",
        editorNotes: article.editorNotes || "",
        authorId: article.authorId,
        editorId: article.editorId,
        createdAt: article.createdAt || null,
      });

      setIsUpdating(true);
      setStatus(article.status);
      setFinalDraftDue(article.finalDraftDue || null);
      setPublishedAt(article.publishedAt || null);
      setScheduledAt(article.scheduledAt || null);

      if (canSchedule && article.scheduledAt) {
        const utc = new Date(article.scheduledAt);
        const local = new Date(utc.getTime() - utc.getTimezoneOffset() * 60000);
        setScheduledDate(local.toISOString().slice(0, 16));
      } else {
        setScheduledDate(null);
      }
    };

    const load = async () => {
      if (isResettingRef.current) return;

      // wait until lookups are loaded
      if (!token || authors.length === 0 || editors.length === 0 || categories.length === 0 || tags.length === 0) return;

      try {
        if (draftSlug) {
          const article = await loadBySlug(draftSlug);
          if (!cancelled) await hydrateStateFromArticle(article);
          return;
        }
        if (postIdParam) {
          const article = await loadById(postIdParam);
          if (!cancelled) await hydrateStateFromArticle(article);
          return;
        }
        // No params: leave Writer’s Room in "new draft" state
      } catch (_) {
        // optional: toast error
      }
    };

    load();
    return () => { cancelled = true; };
  }, [draftSlug, postIdParam, token, authors, editors, categories, tags]);

  useEffect(() => {
    const handleClick = (e) => {
      const editorRoot = quillRef.current?.getEditor()?.root;
      if (!editorRoot || !editorRoot.contains(e.target)) return;

      const blotNode = e.target.closest(".ql-customImage");
      if (!blotNode) return;

      const img = blotNode.querySelector("img");
      if (!img) return;

      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const index = editor.getIndex(Quill.find(blotNode));
      setEditingImageData({
        src: img.getAttribute("src"),
        alt: img.getAttribute("alt"),
        attribution: img.getAttribute("title") || "",
        caption: blotNode.querySelector(".ql-customImage-caption")?.textContent || "",
        variants: blotNode.dataset.variants ? JSON.parse(blotNode.dataset.variants) : [],
        filenameBase: blotNode.dataset.filenameBase || "",
      });
      setEditingImageIndex(index);
      setShowImageModal(true);
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const attach = () => {
      const q = quillRef.current?.getEditor?.();
      if (!q) {
        if (!cancelled) setTimeout(attach, 30);
        return;
      }

      if (q.__h1GuardInstalled) return;
      q.__h1GuardInstalled = true;

      // Downgrade pasted <h1> blocks to header level 2
      q.clipboard.addMatcher("h1", (_node, delta) => {
        delta.ops = (delta.ops || []).map(op => ({
          ...op,
          attributes: { ...(op.attributes || {}), header: 2 },
        }));
        return delta;
      });
    };

    attach();
    return () => { cancelled = true; };
  }, [focusMode, resetKey]);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const editor = quill.root;

    // Disable drag-and-drop image inserts
    const handleDrop = (e) => {
      const hasImage = Array.from(e.dataTransfer?.items || []).some((item) =>
        item.type.startsWith("image/")
      );
      if (hasImage) {
        e.preventDefault();
        toast.warning("Dragging and dropping images is disabled. Please use the upload button.");
      }
    };

    editor.addEventListener("drop", handleDrop);

   return () => {
      editor.removeEventListener("drop", handleDrop);
    };
  }, [focusMode, resetKey]);

  useEffect(() => {
    let detach = null;

    const attach = () => {
      const quill = quillRef.current?.getEditor?.();
      if (!quill) {
        // ReactQuill may not be ready on first render
        setTimeout(attach, 25);
        return;
      }

      if (quill.__pasteImageGuardInstalled) return;
      quill.__pasteImageGuardInstalled = true;

      // Always drop <img> blots from anything that goes through the clipboard
      quill.clipboard.addMatcher("img", () => {
        return new Delta(); // strip the image
      });

      // Only show the toast on an actual user paste
      const root = quill.root; // This is the content editable element

      const handlePaste = (e) => {
        const html = e.clipboardData?.getData("text/html") || "";

        // Only show toast if the pasted HTML actually has an <img>
        if (html.includes("<img")) {
          const now = Date.now();
          if (now - lastPasteImageToastRef.current > 2000) {
            toast.info(
              "We removed images from your pasted content. Please use the image upload button to add images."
            );
            lastPasteImageToastRef.current = now;
          }
        }
      };

      root.addEventListener("paste", handlePaste);

      detach = () => {
        root.removeEventListener("paste", handlePaste);
      };
    };

    attach();

    return () => {
      // cleanup on unmount
      if (detach) detach();
    };
  }, [focusMode, resetKey]);

  useEffect(() => {
    // Only poll when we have an open draft
    const id = activeDraft?._id || currentArticleId;
    if (!id || !token) return;

    const POLL_MS = 8000;

    const stop = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    const tick = async (reason = "interval") => {
      if (inFlightPollRef.current) return;
      inFlightPollRef.current = true;

      try {
        lastPollAtRef.current = Date.now();

        // 1) refresh sidebar lists (optional but usually wanted)
        //    Use in-flight lock so these calls can't overlap.
        await loadArticles();

        // 2) refresh open draft collab state (comments, inline, notes)
        await refreshOpenDraftCollab();
      } catch (e) {
        // keep quiet; this runs often
        // console.debug("[poll] failed", reason, e);
      } finally {
        inFlightPollRef.current = false;
      }
    };

    // Start interval
    stop();
    pollRef.current = setInterval(() => {
      if (document.hidden) return;
      tick("interval");
    }, POLL_MS);

    // Immediate sync on mount/open
    tick("initial");

    // Pause/resume based on tab visibility
    const onVis = () => {
      if (document.hidden) {
        stop();
        return;
      }

      // Resume interval
      if (!pollRef.current) {
        pollRef.current = setInterval(() => tick("interval"), POLL_MS);
      }

      // Catch up immediately when returning to the tab
      tick("visible");
    };

    document.addEventListener("visibilitychange", onVis);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [activeDraft?._id, currentArticleId, token, loadArticles, refreshOpenDraftCollab]);

  const validateFields = () => {
    const content = quillRef.current?.getEditor()?.root?.innerHTML || "";

    if (
      !title ||
      !content ||
      !seoTitle ||
      !metaDescription ||
      !focusKeyphrase ||
      selectedCategories.length === 0 ||
      !postSlug ||
      !mainAuthor ||
      !editor
    ) {
      toast.error("Please fill in all required fields marked with *");
      return false;
    }

    if (containsSanitizedImages(content)) {
      toast.error("One or more pasted images were removed and won’t be saved. Your text is safe. Please use the image upload button to add images again.");
      return false;
    }

    return true;
  };

  const validateSaveFields = () => {
    if (
      !title ||
      !mainAuthor?.value
    ) {
      toast.error("Please give your draft a title before saving. The title can be changed later.");
      return false;
    }

    return true;
  };

  const handleSaveAsDraft = async () => {
    if (!canEditDraft && role < 4) {
      toast.error("This draft is locked in the current stage. Comments and notes only.");
      return;
    }

    if (!validateSaveFields()) return;

    setIsLoading(true);

    try {
     // Hard block: writers/editors cannot schedule drafts, even if state is tampered with
     if (!canSchedule && scheduledDate) {
       setScheduledDate(null);
     }

      const authorId = await getMainAuthor();
      const editorId = editor?.value;
      const categories = await getCategories();
      const tags = await getTags();

      const quillHtml = quillRef.current?.getEditor()?.root?.innerHTML || "";

      // Sanitize images here
      const sanitizedHtml = sanitizeImages(quillHtml);

      // Warn (but don't block) if anything changed
      if (sanitizedHtml !== quillHtml) {
        toast.warn(
          "One or more unauthorized images were removed and won't be saved. To add images, please use the image upload button."
        );
      }

      const normalizedHtml = normalizeBodyHeadings(sanitizedHtml);

      if (normalizedHtml !== sanitizedHtml) {
        toast.info("We converted H1 headings to H2. Use H2 for section headings—your article title is above.");
      }

      const plainDesc = extractText(normalizedHtml);

      const now = new Date();

      const draftIsScheduled =
        canSchedule &&
        status === "draft" &&
        scheduledDate &&
        new Date(scheduledDate) > now;

      const effectiveStage = (canSchedule && draftIsScheduled)
        ? SCHEDULE_PUBLISH_STAGE
        : stage;

      if (draftIsScheduled && stage !== SCHEDULE_PUBLISH_STAGE) {
        setStage(SCHEDULE_PUBLISH_STAGE);
      }

      const newArticle = {
        authorId,
        authors: selectedAuthors,
        editorId,
        stage: effectiveStage,
        estWordCount: estWordCount?.value || "",
        title,
        shortDesc:
          plainDesc.slice(0, 150) + (plainDesc.length > 150 ? "..." : ""),
        desc: normalizedHtml,
        categories,
        tags,
        seoTitle,
        metaDescription,
        focusKeyphrase,
        url,
        status: "draft",
        published: false,
        post_slug: postSlug,
        scheduledAt: (canSchedule && status === "draft" && scheduledDate) ? new Date(scheduledDate) : null,
        finalDraftDue: finalDraftDue || null,
      };

      // Add featured image metadata if available
      const featuredImagePayload = getFeaturedImagePayload(featuredImageData);
      if (featuredImagePayload) {
        Object.assign(newArticle, featuredImagePayload);
      }

      if (isUpdating) {
        await axios.put(`/posts/${currentArticleId}`, newArticle, {
          headers: { Authorization: `Bearer ${token}` },
        });
        await commitManual();
        toast.success("Draft updated successfully!");
      } else {

        const res = await axios.post(`/posts`, newArticle, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const newId = res.data._id;
        setIsUpdating(true);
        setCurrentArticleId(newId);

        // Seed first version (manual) immediately
        const q = quillRef.current?.getEditor?.();
        const delta = q?.getContents();
        const content = (delta && delta.ops?.length) ? delta : normalizedHtml;

        await axios.post(
          `/posts/${newId}/commit`,
          { type: "manual", content },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setActiveDraft({
          _id: newId,
          comments: [],
          inlineComments: [],
          writerNotes: res.data.writerNotes || "",
          editorNotes: res.data.editorNotes || "",
          authorId: res.data.authorId,
          editorId: res.data.editorId,
          createdAt: res.data.createdAt || null,
        });

        toast.success("Draft saved successfully!");
      }

      loadArticles();

    } catch (err) {
      console.error(
        "Error saving draft",
        err.response?.status,
        err.response?.data || err
      );

      const serverMsg =
        err.response?.data?.message || err.response?.data?.error;

      toast.error(serverMsg || "Error saving draft");
    } finally {
      setIsLoading(false);
    }
  };

  const isScheduled = canSchedule && status === "draft" && scheduledDate && new Date(scheduledDate) > new Date();
  const buttonLabel = isScheduled ? "Schedule" : "Publish Now";

  const handlePublish = async () => {
    if (user.role < 4) {
      toast.error("You are not authorized to publish.");
      return;
    }

    if (!validateFields()) return;

    if (seoCriticalIssues.length > 0) {
      toast.dismiss();
      toast.error(
        "Resolve SEO critical issues before publishing:\n• " + seoCriticalIssues.join("\n• ")
      );
      return;
    }

    if (featuredImageData?.src) {
      if (!featuredImageData.alt?.trim() || !featuredImageData.attribution?.trim()) {
        toast.error("Featured image must include alt text and attribution.");
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);

    const authorId = await getMainAuthor();
    const editorId = editor?.value;
    const categories = await getCategories();
    const tags = await getTags();
    const quillHtml = quillRef.current?.getEditor()?.root?.innerHTML || "";

    // Sanitize images here
    const sanitizedHtml = sanitizeImages(quillHtml);

    // Warn (but don't block) if anything changed
    if (sanitizedHtml !== quillHtml) {
      toast.warn(
        "One or more unauthorized images were removed and won't be saved. To add images, please use the image upload button."
      );
    }

    const normalizedHtml = normalizeBodyHeadings(sanitizedHtml);

    if (normalizedHtml !== sanitizedHtml) {
      toast.info("We converted H1 headings to H2. Use H2 for section headings—your article title is above.");
    }

    const plainDesc = extractText(normalizedHtml);

    const publishStage = (canSchedule && isScheduled)
      ? SCHEDULE_PUBLISH_STAGE
      : stage;

    if (isScheduled && stage !== SCHEDULE_PUBLISH_STAGE) {
      setStage(SCHEDULE_PUBLISH_STAGE);
    }

    const newArticle = {
      authorId,
      authors: selectedAuthors,
      editorId,
      stage: publishStage,
      estWordCount: estWordCount?.value || "",
      title,
      shortDesc:
        plainDesc.slice(0, 150) + (plainDesc.length > 150 ? "..." : ""),
      desc: normalizedHtml,
      categories,
      tags,
      seoTitle,
      metaDescription,
      focusKeyphrase,
      url,
      status: isScheduled ? "draft" : "published",
      published: !isScheduled,
      post_slug: postSlug,
      scheduledAt: (canSchedule && isScheduled) ? new Date(scheduledDate) : null,
      finalDraftDue: finalDraftDue || null,
    };

    const featuredImagePayload = getFeaturedImagePayload(featuredImageData);
    if (featuredImagePayload) {
      Object.assign(newArticle, featuredImagePayload);
    }

    const tokenPreview = token?.slice(0, 10) + "...";
    console.log("Token:", tokenPreview);
    console.log("Payload:", newArticle);
    console.log("currentArticleId:", currentArticleId);
    console.log("Authorization header:", `Bearer ${tokenPreview}`);
    console.log("Selected categories:", selectedCategories);
    console.log("Selected editor:", editor);

    try {
      if (isUpdating) {
        console.log("Updating article with ID:", currentArticleId);
        await axios.put(`/posts/${currentArticleId}`, newArticle, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Article updated successfully!");
      } else {
        console.log("Creating new article");
        await axios.post(`/posts`, newArticle, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (isScheduled) {
          toast.success("Article scheduled successfully!");
        } else {
          toast.success("Article published successfully!");
        }
      }

      loadArticles();
    } catch (err) {
      if (err.response) {
        console.error("Error publishing article (response):", err.response.data);
        console.error("Status:", err.response.status);
      } else if (err.request) {
        console.error("Error publishing article (request):", err.request);
      } else {
        console.error("Error publishing article (message):", err.message);
      }
      toast.error(err?.response?.data?.message || "Error publishing article");
    } finally {
      setIsLoading(false);
    }
  };

  const openPreview = async () => {
    if (!postSlug?.trim()) {
      toast.error("Add a slug (URL) before previewing.");
      return;
    }
    // Require a saved draft so the preview reflects the backend
    if (!isUpdating || !currentArticleId) {
      toast.info("Saving draft before preview…");
      await handleSaveAsDraft();
    }
    window.open(`/preview/${postSlug}`, "_blank", "noopener,noreferrer");
  };

  const handleCancelDraft = () => {
    isResettingRef.current = true;
    setResetKey(k => k + 1);

    setTitle("");
    setPostSlug("");
    setSeoTitle("");
    setStage("IDEATION");
    setMetaDescription("");
    setFinalDraftDue(null);
    setEstWordCount(null);
    setDesc("");
    setSeoCriticalIssues([]);
    setShowRestore(false);
    setSavedBadge("");
    setFocusKeyphrase("");
    setCaption("");
    setUrl("");
    setSelectedCategories([]);
    setMainAuthor(null);
    setEditor(null);
    setSelectedAuthors([]);
    setSelectedTags([]);
    setCurrentArticleId(null);
    setFeaturedImageData(null);
    setScheduledDate(null);
    setScheduledAt(null);
    setIsUpdating(false);
    setStatus("draft");
    setPublishedAt(null);

    setActiveDraft(null);
    setInlineComments([]);
    setActiveInlineCommentId(null);
    setHasPendingInlineSel(false);
    setPendingQuote("");
    lastSelectionRef.current = null;

    navigate("/write", { replace: true });

    const quill = quillRef.current?.getEditor();
    if (quill) {
      quill.setContents([]); // clear content
    }
    setEditorHtml(""); // also reset analysis source
    editorHtmlRef.current = "";

    // allow hydration again on next tick
    setTimeout(() => { isResettingRef.current = false; }, 0);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => {
      const next = !prev;
      document.body.style.overflow = next ? "hidden" : "auto";
      return next;
    });
  };

  const toggleTooltip = (id) => {
    setVisibleTooltip((prev) => (prev === id ? null : id));
  };

  const addTagToDatabase = async (newTagLabel) => {
    const label = String(newTagLabel || "").trim();
    const slug = createSlug(label);
    if (!slug) return;

    // Prevent local duplicate option entries immediately
    if (tags.some((t) => String(t.value) === String(slug))) return;

    try {
      const response = await axios.post(
        "/tags",
        { tag_name: label, tag_slug: slug },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 201) {
        const createdTag = response.data;
        setTags((prev) =>
          dedupeByValue([
            ...prev,
            { label: decodeHtml(createdTag.tag_name), value: createdTag.tag_slug },
          ])
        );
      }
    } catch (error) {
      console.error("Error adding tag to the database:", error);
    }
  };

  const handleTagChange = (opts = []) => {
    const normalized = dedupeByValue(opts.map(normalizeTagOpt)).filter(Boolean);

    const existing = new Set(tags.map((t) => String(t.value)));
    normalized.forEach((t) => {
      if (!existing.has(String(t.value))) addTagToDatabase(t.label);
    });

    setSelectedTags(normalized);
  };

  const handleContentImages = useCallback(() => {
    if (!canEditDraft && role < 4) {
      toast.info("This draft is locked in the current stage. Comments and notes only.");
      return;
    }

    setEditingImageData(null);
    setEditingImageIndex(null);
    setShowImageModal(false); // Ensure modal unmounts
    setTimeout(() => {
      setShowImageModal(true);
    }, 0); // Trigger fresh mount with cleared state
  }, [canEditDraft, role]);

  const handleInsertImage = (imageData) => {
    if (!canEditDraft && role < 4) {
      toast.info("This draft is locked in the current stage. Comments and notes only.");
      return;
    }

    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const index = editingImageIndex ?? quill.getSelection(true)?.index ?? 0;

    // Remove old image if editing
    if (editingImageIndex !== null) {
      quill.deleteText(editingImageIndex, 1, "user");
    }

    quill.insertEmbed(index, "customImage", {
      src: imageData.src,
      alt: imageData.alt,
      attribution: imageData.attribution,
      caption: imageData.caption,
      variants: imageData.variants,
      filenameBase: imageData.filenameBase,
    });

    quill.setSelection(index + 1, 0, "user");

    setEditingImageData(null);
    setEditingImageIndex(null);
    setShowImageModal(false);
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [
            { header: [2, 3, false] }, // Shows H2, H3, Normal
            {
              font: [],
            },
          ],
          [{ size: [] }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [
            { list: "ordered" },
            { list: "bullet" },
            { indent: "-1" },
            { indent: "+1" },
          ],
          ["link", "image", "video"],
          ["clean"],
        ],
        handlers: {
          image: handleContentImages,
        },
      },

      clipboard: {
        matchers: [],
      },

      history: {
        delay: 1000,
        maxStack: 100,
        userOnly: true, // don't record programmatic edits
      },
    }),
    [handleContentImages]
  );

  const handlePaste = (e) => {
    // Block pasted image *files* (e.g., screenshots, dragged images)
    if (e.clipboardData?.files?.length) {
      const containsImage = Array.from(e.clipboardData.files).some((file) =>
        file.type.startsWith("image/")
      );
      if (containsImage) {
        e.preventDefault();
        toast.info("Pasting images is disabled. Please use the image upload button instead.");
        return;
      }
    }
  };

  // If we have an ID but activeDraft isn't set yet, fetch and set it.
  useEffect(() => {
    const setupActive = async () => {
      if (!currentArticleId || activeDraft?._id === currentArticleId) return;
      try {
        const [postRes, commentsRes, inlineCommentsRes] = await Promise.all([
          axios.get(`/posts/id/${currentArticleId}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`/posts/${currentArticleId}/comments`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })), // tolerate no comments endpoint
          axios.get(`/posts/${currentArticleId}/inlineComments`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        ]);
        setActiveDraft({
          _id: postRes.data._id,
          comments: commentsRes.data || postRes.data.comments || [],
          inlineComments: inlineCommentsRes.data || postRes.data.inlineComments || [],
          writerNotes: postRes.data.writerNotes || "",
          editorNotes: postRes.data.editorNotes || "",
          authorId: postRes.data.authorId,
          editorId: postRes.data.editorId,
        });

      } catch (e) {
        console.error("Failed to set activeDraft from currentArticleId", e);
      }
    };
    setupActive();
  }, [currentArticleId, token]);

  useEffect(() => {
    const q = quillRef.current?.getEditor?.();
    if (!q) return;

    if (lastQuillInstanceRef.current === q) {
      // same editor → do nothing
      return;
    }

    // new editor instance → allow bindings to install
    lastQuillInstanceRef.current = q;
  }, [focusMode, resetKey]);

  const scopedMentionUsers = useMemo(() => {
    if (!activeDraft?._id) return [];

    const authorIds = [activeDraft.authorId, activeDraft.authorMongoId]
      .filter(Boolean)
      .map(String);

    const editorIds = [activeDraft.editorId, activeDraft.editorMongoId]
      .filter(Boolean)
      .map(String);

    return mentionUsers.filter((u) => {
      const uid = String(u.id);

      // Draft owners
      if (authorIds.includes(uid)) return true;
      if (editorIds.includes(uid)) return true;

      // Lead Editors / Admins
      if (Number(u.role) >= 4) return true; // adjust threshold if needed

      return false;
    });
  }, [mentionUsers, activeDraft]);

  if (isAuthorized === null) return null; // or a small loading UI
  if (isAuthorized === false) return <UnauthorizedComponent />;

  const unscheduledDrafts = drafts.filter(
    (draft) => draft.status === "draft" && (!draft.scheduledAt)
  );

  const scheduledDrafts = drafts.filter(
    (draft) => draft.status === "draft" && draft.scheduledAt
  );

  const visibleDrafts = unscheduledDrafts.filter((d) => {
    if (user.role >= 4) return true;

    const uidCandidates = [user?._id, user?.id, user?.userId].filter(Boolean).map(String);

    if (user.role === 3) {
      const editorCandidates = [d.editorId, d.editorMongoId, d.editor?._id, d.editor?.userId]
        .filter(Boolean).map(String);
      return uidCandidates.some(u => editorCandidates.includes(u));
    }

    if (user.role === 2) {
      const authorCandidates = [d.authorId, d.authorMongoId, d.author?._id, d.author?.userId]
        .filter(Boolean).map(String);
      return uidCandidates.some(u => authorCandidates.includes(u));
    }

    return false;
  });

  const runInlineFeedback = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const text = quill.getText();
    const length = quill.getLength();

    // Clear old formatting
    quill.formatText(0, length, "passive-voice", false);
    quill.formatText(0, length, "long-sentence", false);

    // Detect issues
    const passiveMatches = detectPassiveVoice(text);
    const longSentenceMatches = detectLongSentences(text);

    // Apply passive voice formatting first
    passiveMatches.forEach(({ start, end, message }) => {
      quill.formatText(start, end - start, "passive-voice", message);
    });

    // Apply long sentence formatting only if no passive already exists
    longSentenceMatches.forEach(({ start, end, message }) => {

      // Build subranges that exclude passive spans
      let ranges = [{ start, end }];

      passiveMatches.forEach(({ start: pStart, end: pEnd }) => {
        ranges = ranges.flatMap(({ start: rStart, end: rEnd }) => {
          if (pEnd <= rStart || pStart >= rEnd) {
            // No overlap
            return [{ start: rStart, end: rEnd }];
          }

          const segments = [];
          if (pStart > rStart) {
            segments.push({ start: rStart, end: pStart });
          }
          if (pEnd < rEnd) {
            segments.push({ start: pEnd, end: rEnd });
          }
          return segments;
        });
      });

      // Apply long-sentence formatting to non-overlapping parts
      ranges.forEach(({ start, end }) => {
        if (end > start) {
          quill.formatText(start, end - start, "long-sentence", message);
        }
      });
    });

    setHasInlineFeedback(true);
  };

  // Meta guard: block meta edits when locked (writers/editors), allow leads/admins
  const guardMeta = (fn) => (...args) => {
    if (isReadOnlyDraft && role < 4) {
      toast.info("Locked in this stage. Comments and notes only.");
      return;
    }
    return fn(...args);
  };

  // "Safe" setters to pass into <MetaData />
  const setTitleSafe = guardMeta(setTitle);
  const setPostSlugSafe = guardMeta(setPostSlug);

  const setSeoTitleSafe = guardMeta(setSeoTitle);
  const setMetaDescriptionSafe = guardMeta(setMetaDescription);
  const setFocusKeyphraseSafe = guardMeta(setFocusKeyphrase);

  const setSelectedAuthorsSafe = guardMeta(setSelectedAuthors);
  const setMainAuthorSafe = guardMeta(setMainAuthor);
  const setEditorSafe = guardMeta(setEditor);
  const setEstWordCountSafe = guardMeta(handleEstWordCountChange);

  const setSelectedCategoriesSafe = guardMeta(setSelectedCategories);
  const setSelectedTagsSafe = guardMeta(setSelectedTags);

  // If you allow manual stage changes in MetaData, guard that too:
  const setStageSafe = guardMeta(setStage);

  // This prop is referenced below; define it to avoid runtime/compile issues.
  const canEditMeta = !isReadOnlyDraft || role >= 4;

const renderDraftActionBar = () => (
  <div className="draftActionBar">
    <div className="draftActions-left">
                    <a
                      href="/style-guide"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="style-guide-nav-btn"
                    >
                      Style Guide
                    </a>

                    {canAttemptDelete && (
                      <FontAwesomeIcon
                        icon={faTrashCan}
                        className="draftDeleteIcon"
                        title="Delete Draft"
                        onClick={() => {
                          const idToDelete = activeDraft?._id || currentArticleId;
                          if (!idToDelete) return;

                          const draft = normalizeDraftIdsForDelete(
                            drafts.find(d => String(d._id) === String(idToDelete)) ||
                            (String(activeDraft?._id) === String(idToDelete) ? activeDraft : null)
                          );

                          if (!canDeleteDraft(draft)) {
                            toast.error("You can only delete drafts assigned to you.");
                            return;
                          }

                          setDeleteType("draft");
                          setConfirmDeleteId(idToDelete);
                        }}
                      />
                    )}

                    {user?.role >= 2 && user?.role <= 3 && (
                      <button
                        className="writeSubmit"
                        onClick={handleStageSubmit}
                        disabled={isLoading || !canStageSubmit}
                        title={!canStageSubmit ? "This draft is not in your stage." : undefined}
                      >
                        {isLoading ? "Processing..." : getSubmitLabelForStage(stage)}
                      </button>
                    )}

                    {user?.role >= 4 && (
                      <>
                        {status !== "published" ? (
                          <div className="scheduleWrap">
                            <span title="Schedule for future publication">
                              <FaCalendarAlt
                                title={hasCriticalSeo ? "Fix critical SEO issues before scheduling" : "Schedule for future publication"}
                                onClick={() => {
                                  if (hasCriticalSeo) {
                                    toast.error("Fix critical SEO issues before scheduling.");
                                    return;
                                  }
                                  const el = scheduleInputRef.current;
                                  if (!el) return;
                                  el.focus();
                                  if (typeof el.showPicker === "function") el.showPicker();
                                  else el.click(); // fallback
                                }}
                                style={{
                                  fontSize: "20px",
                                  cursor: hasCriticalSeo ? "default" : "pointer",
                                  color: scheduledDate ? "green" : "#333",
                                  opacity: hasCriticalSeo ? 0.5 : 1,
                                }}
                              />
                            </span>
                            <input
                              ref={scheduleInputRef}
                              id="schedule-input"
                              type="datetime-local"
                              value={scheduledDate || ""}
                              onChange={(e) => setScheduledDate(e.target.value)}
                              disabled={hasCriticalSeo}
                              style={{
                                position: "absolute",
                                opacity: 0,
                                width: "32px",
                                height: "32px",
                                left: 0,
                                top: 0,
                              }}
                            />
                            {scheduledDate && (
                              <span className="scheduleText">
                                Scheduled for: {new Date(scheduledDate).toLocaleString()}
                              </span>
                            )}
                            {scheduledDate && (
                              <FaTimes
                                onClick={() => {
                                  setScheduledDate(null);
                                  if (scheduleInputRef.current) scheduleInputRef.current.value = "";
                                }}
                                style={{
                                  color: "#888",
                                  fontSize: "14px",
                                  cursor: "pointer",
                                  transition: "color 0.2s",
                                }}
                                title="Clear scheduled date"
                                onMouseEnter={(e) => (e.currentTarget.style.color = "#d00")}
                                onMouseLeave={(e) => (e.currentTarget.style.color = "#888")}
                              />
                            )}
                          </div>
                        ) : publishedAt ? (
                          <div style={{ fontSize: "14px", color: "#555" }}>
                            Published on {new Date(publishedAt).toLocaleString()}
                          </div>
                        ) : null}
                      </>
                    )}
    </div>


    <div className="draftActions-right">
                    {user?.role >= 4 && (
                      <>
                        <button
                          className="writeSubmit"
                          onClick={() => setShowPublishConfirm(true)}
                          disabled={isLoading || hasCriticalSeo}
                        >
                          {isLoading ? "Processing..." : buttonLabel}
                        </button>
                      </>
                    )}

                    <button
                      className="writeSubmitDraft"
                      onClick={handleSaveAsDraft}
                      disabled={isLoading || (role < 4 && isReadOnlyDraft)}
                      title={(role < 4 && isReadOnlyDraft) ? "Comments and notes only in this stage." : undefined}
                    >
                      {isLoading ? "Saving..." : "Save as Draft"}
                    </button>

                    {lastCommitAt && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: "#666" }}>
                        {savedBadge || `Saved • ${Math.round((Date.now()-lastCommitAt.getTime())/1000)}s ago`}
                      </span>
                    )}

                    {(lastManual || (autosaves && (autosaves?.length ?? 0) > 0)) ? (
                      <div style={{ position: "relative", display: "inline-block", marginLeft: 8 }}>
                        <button
                          type="button"
                          className="writePreviewBtn"
                          onClick={() => setShowRestore(v => !v)}
                          disabled={!currentArticleId}
                          title="Restore a previous version"
                        >
                          Restore
                        </button>

                        {showRestore && (
                          <div
                            style={{
                              position: "absolute",
                              top: "110%",
                              right: 0,
                              zIndex: 20,
                background: "#fff",
                border: "1px solid #ddd",
                borderRadius: 8,
                padding: 8,
                width: 260,
                boxShadow: "0 8px 20px rgba(0,0,0,0.12)"
              }}
            >
              {lastManual && (
                <div
                  style={{ padding: "6px 8px", cursor: "pointer" }}
                  onClick={() => { setShowRestore(false); restoreVersion(lastManual.id); }}
                >
                  <strong>Last manual save</strong> — {fmtTs(lastManual.ts)}
                </div>
              )}

              {(autosaves || []).map(a => (
                <div
                  key={a.id}
                  style={{ padding: "6px 8px", cursor: "pointer" }}
                  onClick={() => { setShowRestore(false); restoreVersion(a.id); }}
                >
                  Autosave — {fmtTs(a.ts)}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <span>Save to enable restore.</span>
      )}

      <button
        type="button"
        className="writePreviewBtn"
        onClick={openPreview}
        disabled={!postSlug?.trim() || isLoading}
      >
        Preview
      </button>
      <button
        type="button"
        className="info-icon"
        onClick={() => toggleTooltip("preview")}
        aria-label="Preview help"
        style={{ marginLeft: "8px" }}
      >
        i
      </button>
      {visibleTooltip === "preview" && (
        <span className="tooltip">Preview shows the last saved version.</span>
      )}
    </div>
  </div>
);

  return (
   <>
    <FeatureImageUploadModal
      isOpen={showFeatureImageModal}
      onClose={() => setShowFeatureImageModal(false)}
      onConfirm={(data) => {
        setFeaturedImageData(data);
        setShowFeatureImageModal(false);
      }}
      initialData={featuredImageData}
      isEditMode={!!featuredImageData}
    />

    <ImageUploadModal
      isOpen={showImageModal}
      onClose={() => {
        setShowImageModal(false);
        setEditingImageData(null);
        setEditingImageIndex(null);
      }}
      onInsert={handleInsertImage}
      initialData={editingImageData}
      isEditMode={!!editingImageData}
    />

    {!focusMode ? (

    <div className="Write">
      <SidebarDraftManager
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        visibleDrafts={visibleDrafts}
        scheduledDrafts={scheduledDrafts}
        articles={articles}
        user={user}
        handleEditDraft={handleEditDraft}
        handleEditArticle={handleEditArticle}
        handleSwitchToPublish={handleSwitchToPublish}
        handleSwitchToDraft={handleSwitchToDraft}
        setDeleteType={setDeleteType}
        setConfirmDeleteId={setConfirmDeleteId}
      />

      <div className="write">
        {getStageBanner() && (
          <div
            style={{
              background: "#FFF4D6",
              border: "1.5px solid #FFB74D",
              padding: "14px 18px",
              margin: "20px 0",
              borderRadius: "8px",
              color: "#5C3B00",
              fontSize: "15px",
              fontWeight: 500,
              lineHeight: "1.5",
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)", // subtle lift
              borderLeft: "5px solid #FFB300",
            }}
          >
            <span style={{ fontSize: "20px", lineHeight: "1" }}>⚠️</span>
            <span>{getStageBanner()}</span>
          </div>
        )}
        <div
          className="write-title"
          style={{ position: "relative", display: "flex", alignItems: "center", gap: "40px" }}
        >
          <button
            style={{
              width: "127px",
              height: "32px",
              borderRadius: "10px",
              backgroundColor: "#272635",
              color: "#B1E5F2",
              border: "none",
              cursor: "pointer",
              fontSize: "15px",
            }}
            onClick={handleCancelDraft}
          >
            + NEW ARTICLE
          </button>

          <button
            style={{
              width: "110px",
              height: "32px",
              borderRadius: "10px",
              backgroundColor: "#B1E5F2",
              color: "#272635",
              border: "none",
              cursor: "pointer",
              fontSize: "15px",
            }}
            onClick={() => setIsSidebarOpen(true)}
            title="View your drafts"
            aria-label="View your drafts"
          >
            My Drafts
          </button>

          <p
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              margin: 0,
            }}
          >
            The Writer's Room
          </p>
          <button
            style={{
              position: "absolute",
              left: "100%",
              transform: "translateX(-100%)",
              margin: 0,
              width: "210px",
              height: "32px",
              borderRadius: "10px",
              backgroundColor: "#B1E5F2",
              color: "#272635",
              border: "none",
              cursor: "pointer",
              fontSize: "15px",
            }}
            onClick={() => navigate("/edit")}
          >
            EDITORIAL DASHBOARD &#x279C;
          </button>
        </div>

        <form className="writeForm" onSubmit={(e) => e.preventDefault()}>
          {renderDraftActionBar()}

          <div className="writeGrid">

            {/* LEFT */}
            <div className="writeCol writeCol--left">
              <div className="writeCard">
                <div className="writeCard__title">Article Details</div>
              <MetaData
                mainAuthor={mainAuthor}
                setMainAuthor={setMainAuthorSafe}
                selectedAuthors={selectedAuthors}
                setSelectedAuthors={setSelectedAuthorsSafe}
                authors={authors}
                editor={editor}
                setEditor={setEditorSafe}
                editors={editors}
                estWordCount={estWordCount}
                setEstWordCount={setEstWordCountSafe}
                title={title}
                setTitle={setTitleSafe}
                seoTitle={seoTitle}
                setSeoTitle={setSeoTitleSafe}
                metaDescription={metaDescription}
                setMetaDescription={setMetaDescriptionSafe}
                focusKeyphrase={focusKeyphrase}
                setFocusKeyphrase={setFocusKeyphraseSafe}
                postSlug={postSlug}
                setPostSlug={setPostSlugSafe}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategoriesSafe}
                categories={categories}
                tags={tags}
                selectedTags={selectedTags}
                handleTagChange={handleTagChange}
                featuredImageData={featuredImageData}
                setFeaturedImageData={setFeaturedImageData}
                setShowFeatureImageModal={setShowFeatureImageModal}
                toggleTooltip={toggleTooltip}
                visibleTooltip={visibleTooltip}
                finalDraftDue={finalDraftDue}
                stage={stage}
                setStage={setStageSafe}
                user={user}
                token={token}
                currentArticleId={currentArticleId}
                locked={isReadOnlyDraft}
                canEditMeta={canEditMeta}
              />
              </div>
            </div>

            {/* MIDDLE */}
            <div className="writeCol writeCol--middle">
              <div className="writeCard writeCard--content">

              <EditorBlock
                key={resetKey}
                quillRef={quillRef}
                isReadOnlyDraft={isReadOnlyDraft}
                handlePaste={handlePaste}
                modules={modules}
                suggestionMode={suggestionMode}
                setSuggestionMode={setSuggestionMode}
                suggestionModeRef={suggestionModeRef}
                acceptAllSuggestions={acceptAllSuggestions}
                rejectAllSuggestions={rejectAllSuggestions}
                onAcceptPointerDown={onAcceptPointerDown}
                onRejectPointerDown={onRejectPointerDown}
                handleUndo={handleUndo}
                handleRedo={handleRedo}
                canUndo={canUndo}
                canRedo={canRedo}
                toggleTooltip={toggleTooltip}
                visibleTooltip={visibleTooltip}
                wordCount={wordCount}
                hasInlineFeedback={hasInlineFeedback}
                setHasInlineFeedback={setHasInlineFeedback}
                runInlineFeedback={runInlineFeedback}
                primeInlineComment={primeInlineComment}
                currentArticleId={currentArticleId}
                headerRight={focusToggle}
                isFocusMode={false}
              />

              </div>
            </div>

            {/* RIGHT */}
            <div className="writeCol writeCol--right">
              <div className="writeCard">
                <div className="writeCard__title">Inline Comments</div>
              <InlineCommentPanel
                draft={activeDraft || { _id: null, inlineComments: [] }}
                canPost={!!currentArticleId}
                mentionUsers={scopedMentionUsers}
                onAddInlineComment={addInlineComment}
                onDeleteInlineComment={deleteInlineComment}
                currentUser={user}
                hasSelection={hasPendingInlineSel}
                onClearInlineSelection={clearInlineSelection}
                pendingQuote={pendingQuote}
                activeInlineCommentId={activeInlineCommentId}
                onSelectInlineComment={handleSelectInlineComment}
              />
              </div>
            </div>
          </div>
        </form>

        {confirmDeleteId && (
          <ConfirmModal
            message={`Are you sure you want to permanently delete this ${deleteType}?`}
            onConfirm={() => {
              if (deleteType === "draft") {
                handleDeleteDraft(confirmDeleteId);
              } else {
                handleDeleteArticle(confirmDeleteId);
              }
              setConfirmDeleteId(null);
              setDeleteType("");
            }}
            onCancel={() => {
              setConfirmDeleteId(null);
              setDeleteType("");
            }}
          />
        )}

        {showPublishConfirm && (
          <ConfirmModal
            message={`Are you sure you want to ${isScheduled ? "schedule" : "publish"} this article?`}
            onConfirm={async () => {
              setShowPublishConfirm(false);
              await handlePublish();
            }}
            onCancel={() => {
              setShowPublishConfirm(false);
            }}
          />
        )}

        <div className="collaboration-panel">
          <CollaborationPanel
            draft={activeDraft || { _id: null, comments: [], inlineComments: [], writerNotes: "", editorNotes: "" }}
            canPost={!!activeDraft?._id}
            mentionUsers={scopedMentionUsers}
            onAddComment={addComment}
            onDeleteComment={deleteComment}
            onAddInlineComment={addInlineComment}
            onDeleteInlineComment={deleteInlineComment}
            onSaveWriterNotes={saveWriterNotes}
            onSaveEditorNotes={saveEditorNotes}
            currentUser={user}
            canEditWriterNotes={canEditWriterNotes}
            canEditEditorNotes={canEditEditorNotes}
          />
        </div>

        {editorHtml && (
          <>
            {user?.role >= 3 && (
              <>
                <div className="analyser-card">
                  <aside style={{ marginTop: 16 }}>
                    <GrammarAssistantPanel
                      getArticleText={() => quillRef.current?.getEditor().getText()}
                      onIssueClick={handleGrammarIssueClick}
                    />
                  </aside>
                </div>
                <div className="analyser-card">
                  <aside style={{ marginTop: 16 }}>
                    <WriterCoachPanel
                      getArticleText={() => quillRef.current?.getEditor().getText()}
                      title={title}
                      categories={selectedCategories}
                      keyphrase={focusKeyphrase}
                      stageKey={stage}
                    />
                  </aside>
                </div>
              </>
            )}
            <div className="analyser-card">
              <SeoAnalyser
                paused={suggestionMode}
                seoTitle={seoTitle}
                metaDescription={metaDescription}
                focusKeyphrase={focusKeyphrase}
                content={editorHtml}
                postSlug={postSlug}
                caption={caption}
                previousArticles={previousArticlesMemo}
                featuredImageUrl={featuredImageData?.src}
                onCriticalIssuesChange={setSeoCriticalIssues}
                currentArticleId={currentArticleId}
                stageKey={stage}
                currentUserRole={user?.role}
              />
            </div>
            <div className="analyser-card">
              <ReadabilityAnalyser
                content={editorHtml}
                stageKey={stage}
                currentUserRole={user?.role}
              />
            </div>
            <div className="analyser-card">
              <aside style={{ marginTop: 16 }}>
                <MostUsedWords
                  content={editorHtml}
                  topN={10}
                  minLen={3}
                  showBigrams={true}
                  showTrigrams={true}
                  showQuadgrams={true}
                  showPentagrams={true}
                />
              </aside>
            </div>
          </>
        )}
      </div>
    </div>
    ) : (
      <FocusModeOverlay
        isOpen={focusMode}
        onClose={closeFocus}
        title="Focus mode"
      >
        <div className="editorShell--focus" style={{ height: "100%" }}>
          <EditorBlock
            key={resetKey}
            quillRef={quillRef}
            isReadOnlyDraft={isReadOnlyDraft}
            handlePaste={handlePaste}
            modules={modules}
            suggestionMode={suggestionMode}
            setSuggestionMode={setSuggestionMode}
            suggestionModeRef={suggestionModeRef}
            acceptAllSuggestions={acceptAllSuggestions}
            rejectAllSuggestions={rejectAllSuggestions}
            onAcceptPointerDown={onAcceptPointerDown}
            onRejectPointerDown={onRejectPointerDown}
            handleUndo={handleUndo}
            handleRedo={handleRedo}
            canUndo={canUndo}
            canRedo={canRedo}
            toggleTooltip={toggleTooltip}
            visibleTooltip={visibleTooltip}
            wordCount={wordCount}
            hasInlineFeedback={hasInlineFeedback}
            setHasInlineFeedback={setHasInlineFeedback}
            runInlineFeedback={runInlineFeedback}
            primeInlineComment={primeInlineComment}
            currentArticleId={currentArticleId}
            headerRight={focusToggle}
            isFocusMode={true}
            topbarRight={<span className="focusStatus">{isSaving ? "Saving…" : "Saved"}</span>}
          />
        </div>
      </FocusModeOverlay>
    )}

   </>
  );
}
